/**
 * Writing on the weekly scroll from the open board (docs/weekly.md "Writing on it"; owner,
 * 2026-10-05: "starting Friday evening until Saturday night, everyone should be able to write down
 * appreciations and accomplishments that will show up on the this week scroll Sunday at 5am").
 *
 * useWeeklyBoard gives the notice board its lead and the board its composer sheet:
 * - While the window is open, this week's scroll in progress with everyone's lines so far, "Add
 *   yours" at the foot of the paper and a small ink pencil on the lines this person may change (their
 *   own; a steward's, every one). "Add yours" opens the composer in a sheet over the board, the way
 *   the add slip opens the note composer (owner, 2026-10-05: "The location where the form to enter
 *   stuff for the note page is confusing, should be a modal that opens over it"; ComposerSheet):
 *   Appreciation or Accomplishment, one field, the count, Cancel and "Write it", live, saying what is
 *   missing (shared/form-missing.ts weeklyMissing). Written, the line is on the scroll, the sheet
 *   closes and the focus goes back to what opened it ("Add yours", or the pencil). The line earns its
 *   writer GOLD.scrollLine (owner, 2026-10-05: "Posting an appreciation or accomplishment yields 10
 *   gold"): `award` hands the board its gold toast ("+10 Written on the scroll.") and the account's
 *   gold is read again, as the wardrobe does after a buy (src/account-stats.ts).
 * - Five lines a person a week, and a line once written stays (owner, 2026-10-05: "Max of 5 scroll
 *   notes per week, and notes cannot be removed only edited, to prevent abuse"): once this person's
 *   lines are written (shared/weekly.ts linesLeft, from the server's `mine`), "Add yours" gives way to
 *   a quiet "Your five lines are written."; a refusal at the cap (another device wrote the fifth)
 *   says the same in the composer. The pencil opens the same composer on the line, its kind and
 *   words in place, "Save it" for "Write it"; saved, the line changes where it stands, no toast, no gold.
 * - From Saturday night until the scroll is written on Sunday at 5 am, the same scroll without them.
 * - Otherwise the newest finished scroll once this device has seen it in its sheet, else nothing
 *   (shared/weekly.ts weeklyLead).
 * The lines are read on every board load (`refreshKey`); no live push.
 */
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import { refreshAccountStats } from "../account-stats";
import { ErrorBox } from "./Shell";
import { BusyLabel } from "./BusyLabel";
import { ComposerSheet, holdKeyboard } from "./ComposerSheet";
import { WeeklyScroll, type ScrollEntry } from "./WeeklyScroll";
import { MentionField } from "./MentionField";
import { canDelete } from "../../shared/permissions.ts";
import { GOLD } from "../../shared/bounty.ts";
import { weeklyMissing, type Missing } from "../../shared/form-missing.ts";
import type { Role } from "../../shared/types";
import {
  WEEKLY_CAP_REACHED,
  WEEKLY_ENTRY_MAX,
  WEEKLY_KIND_OPTIONS,
  WEEKLY_PLACEHOLDER,
  WEEKLY_SAVE,
  WEEKLY_SUBMIT,
  linesLeft,
  scrollLines,
  scrollSummary,
  weeklyLead,
  type WeeklyEntriesResponse,
  type WeeklyEntry,
  type WeeklyKind,
  type WeeklyScroll as WeeklyScrollData,
  type WeeklyLead as WeeklyLeadData,
} from "../../shared/weekly.ts";
import { radioKeys } from "../radio-group";

/** The window and lines this page last read: the board paints from them on the next visit, then refreshes. */
let remembered: WeeklyEntriesResponse | null = null;

/** Tests only. */
export function forgetWeeklyEntries(): void {
  remembered = null;
}

/** The boards showing the window, told when the workshop reads it again. */
const listeners = new Set<(r: WeeklyEntriesResponse) => void>();

/**
 * Reads this week's window and lines again now, and hands them to any board showing, so the
 * workshop's "Open the scroll for writing" shows "Add yours" (or takes it away) without a reload.
 * A failed read keeps what was shown.
 */
export function refreshWeeklyEntries(): Promise<void> {
  return api.weekly.entries().then(
    (r) => {
      remembered = r;
      for (const l of listeners) l(r);
    },
    () => {},
  );
}

/** This week's window and lines while `enabled`, read again whenever `refreshKey` changes (each board load). */
function useWeeklyEntries(enabled: boolean, refreshKey: unknown): [WeeklyEntriesResponse | null, (fn: (cur: WeeklyEntriesResponse | null) => WeeklyEntriesResponse | null) => void] {
  const [state, setState] = useState<WeeklyEntriesResponse | null>(enabled ? remembered : null);
  useEffect(() => {
    if (!enabled) return;
    listeners.add(setState);
    return () => {
      listeners.delete(setState);
    };
  }, [enabled]);
  useEffect(() => {
    if (!enabled) {
      setState(null);
      return;
    }
    let live = true;
    api.weekly.entries().then(
      (r) => {
        remembered = r;
        if (live) setState(r);
      },
      () => {
        /* offline: keep what was shown */
      },
    );
    return () => {
      live = false;
    };
  }, [enabled, refreshKey]);
  const update = (fn: (cur: WeeklyEntriesResponse | null) => WeeklyEntriesResponse | null) =>
    setState((cur) => {
      const next = fn(cur);
      remembered = next;
      return next;
    });
  return [state, update];
}

export function useWeeklyBoard({
  enabled,
  peonName,
  role,
  finished,
  seen,
  refreshKey,
}: {
  enabled: boolean;
  peonName: string;
  role: Role;
  /** The newest finished scroll (src/components/DoneExtras.tsx useWeeklyScroll). */
  finished: WeeklyScrollData | null;
  /** The device's bookmark (src/weekly-sheet.ts). */
  seen: string | null;
  refreshKey: unknown;
}): {
  lead: ComponentChildren;
  sheet: ComponentChildren;
  award: number | null;
  dismissAward: () => void;
  /** Which scroll leads the notice board, and whether this week's window has been read: a name tag's link waits on them (shared/mentions.ts boardFocusAction). */
  leadData: WeeklyLeadData;
  loaded: boolean;
} {
  const [current, setCurrent] = useWeeklyEntries(enabled, refreshKey);
  const lead = enabled ? weeklyLead(current?.window ?? null, finished, seen) : null;
  // The window is open: lines can be written and changed.
  const canAdd = lead?.kind === "writing" && lead.canAdd;
  // This person's lines are all written: "Add yours" gives way to the quiet line.
  const full = linesLeft(current?.mine) === 0;

  const [open, setOpen] = useState(false);
  /** A fresh sheet each time it opens. */
  const [openKey, setOpenKey] = useState(0);
  // The line the composer is changing, or null when it writes a new one.
  const [editing, setEditing] = useState<WeeklyEntry | null>(null);
  const [kind, setKind] = useState<WeeklyKind>("appreciation");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The gold a line just written earned, for the board's toast; cleared when the toast goes.
  const [award, setAward] = useState<number | null>(null);
  const dismissAward = useCallback(() => setAward(null), []);
  const unready = weeklyMissing({ draft });
  const [missing, setMissing] = useState<Missing | null>(null);
  useEffect(() => {
    if (missing && !unready) setMissing(null);
  }, [missing, unready]);

  // The composer's sheet takes the focus to its field as it opens, Escape and the dim are its Cancel,
  // and once it has closed the focus goes back to what opened it: "Add yours", or the pencil (or,
  // the pencil gone, "Add yours"), the board not scrolling for it.
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const opened = useRef(false);
  useEffect(() => {
    if (open) {
      opened.current = true;
    } else if (opened.current) {
      opened.current = false;
      const back = openerRef.current?.isConnected ? openerRef.current : addRef.current;
      back?.focus({ preventScroll: true });
    }
  }, [open]);
  // The window closing while the composer is open closes it.
  useEffect(() => {
    if (!canAdd && open) setOpen(false);
  }, [canAdd, open]);

  /** The sheet has gone (Cancel, the dim, Escape, or a line written or changed): nothing of it is kept. */
  function closed() {
    setOpen(false);
    setEditing(null);
    setDraft("");
    setKind("appreciation");
    setError(null);
    setMissing(null);
  }

  function show(opener: HTMLElement | null) {
    holdKeyboard();
    openerRef.current = opener;
    setError(null);
    setMissing(null);
    setOpenKey((k) => k + 1);
    setOpen(true);
  }

  function startNew() {
    if (open) return;
    setEditing(null);
    setDraft("");
    setKind("appreciation");
    show(addRef.current);
  }

  // The pencil: the composer on this line, its kind and words in place.
  function startEdit(line: ScrollEntry, opener?: HTMLElement) {
    if (!line.id || open) return;
    const entry = line as WeeklyEntry;
    setEditing(entry);
    setKind(entry.kind);
    setDraft(entry.text);
    show(opener ?? null);
  }

  async function write(close: (done?: boolean) => void) {
    if (busy) return;
    if (unready) {
      setError(null);
      setMissing(unready);
      fieldRef.current?.focus();
      return;
    }
    void playAcknowledgement();
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        // A change: the line where it stands, no toast, no gold.
        const changed = await api.weekly.update(editing.id, { kind, text: draft.trim() });
        setCurrent((cur) => (cur ? { ...cur, entries: cur.entries.map((e) => (e.id === changed.id ? changed : e)) } : cur));
        close(true);
        return;
      }
      const entry = await api.weekly.add({ kind, text: draft.trim() });
      setCurrent((cur) => (cur && !cur.entries.some((e) => e.id === entry.id) ? { ...cur, entries: [...cur.entries, entry], mine: (cur.mine ?? 0) + 1 } : cur));
      close(true);
      setAward(GOLD.scrollLine);
      void refreshAccountStats(peonName).catch(() => {});
    } catch (e) {
      const message = e instanceof Error ? e.message : "Could not write that";
      setError(message);
      // Another device wrote the last line: read the scroll again, so Add yours gives way.
      if (message === WEEKLY_CAP_REACHED) void refreshWeeklyEntries();
    } finally {
      setBusy(false);
    }
  }

  const chars = Array.from(draft).length;
  const leadNode =
    lead?.kind === "finished" ? (
      <WeeklyScroll lines={scrollLines(lead.scroll.data)} summary={scrollSummary(lead.scroll.data)} />
    ) : lead?.kind === "writing" ? (
      <WeeklyScroll
        lines={current?.entries ?? []}
        summary={scrollSummary(current)}
        add={canAdd ? { onAdd: startNew, expanded: open && !editing, addRef, full } : undefined}
        mayEdit={canAdd ? (l) => canDelete({ name: peonName, role }, l.name) : undefined}
        onEdit={canAdd ? startEdit : undefined}
      />
    ) : null;

  const sheet =
    open && canAdd ? (
      <ComposerSheet key={openKey} label={WEEKLY_KIND_OPTIONS.find((o) => o.value === kind)!.label} onClosed={closed}>
        {(close) => (
          <div class="note-composer weekly-composer">
            <div class="segmented note-chips" role="radiogroup" aria-label="Kind" onKeyDown={radioKeys}>
              {WEEKLY_KIND_OPTIONS.map((o) => (
                <button key={o.value} type="button" role="radio" aria-checked={kind === o.value} class={kind === o.value ? "active" : ""} onClick={() => setKind(o.value)}>
                  {o.label}
                </button>
              ))}
            </div>
            <label class="sr-only" for="weekly-composer-text">
              {WEEKLY_KIND_OPTIONS.find((o) => o.value === kind)!.label}
            </label>
            <MentionField
              as="textarea"
              fieldRef={fieldRef}
              pickerId="weekly-composer"
              id="weekly-composer-text"
              class="input"
              rows={2}
              maxLength={WEEKLY_ENTRY_MAX}
              placeholder={WEEKLY_PLACEHOLDER[kind]}
              value={draft}
              onValue={setDraft}
              data-autofocus
            />
            <div class="note-composer-foot">
              <span class="muted small">
                {chars} / {WEEKLY_ENTRY_MAX}
              </span>
              <div class="note-composer-actions">
                <button type="button" class="btn-quiet" onClick={() => close()}>
                  Cancel
                </button>
                <button type="button" class={`btn btn-primary${unready ? " btn-unready" : ""}`} onClick={() => void write(close)} disabled={busy} aria-describedby={missing ? "weekly-missing" : undefined}>
                  <BusyLabel busy={busy} label={editing ? "Saving…" : "Writing…"}>
                    {editing ? WEEKLY_SAVE : WEEKLY_SUBMIT}
                  </BusyLabel>
                </button>
              </div>
            </div>
            {missing && unready ? (
              <div class="form-error" role="alert" id="weekly-missing">
                {unready.line}
              </div>
            ) : (
              error && <ErrorBox message={error} />
            )}
          </div>
        )}
      </ComposerSheet>
    ) : null;
  return { lead: leadNode, sheet, award, dismissAward, leadData: lead, loaded: current !== null };
}
